import { useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription } from "../../../app/components/ui/alert";
import { Badge } from "../../../app/components/ui/badge";
import { Button } from "../../../app/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../app/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api, ApiError } from "../../../lib/api";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";

export type StockBatchRow = {
  id: string;
  received_at: string | null;
  quantity_remaining: string;
  selling_price: string;
  mrp: string;
  purchase_price: string;
};

type Props = {
  open: boolean;
  productId: string;
  productName: string;
  currentOverrideBatchId?: string | null;
  isOverride?: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => Promise<void> | void;
};

function qtyToNumber(q: string) {
  const n = Number(q);
  return Number.isFinite(n) ? n : 0;
}

export function PosBatchOverrideModal({
  open,
  productId,
  productName,
  currentOverrideBatchId,
  isOverride,
  onOpenChange,
  onDone,
}: Props) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState<StockBatchRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!token) return;

    setLoading(true);
    setError(null);
    api.products
      .listStockBatches({ token, productId })
      .then((res) => setRows(res.data))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load batches"))
      .finally(() => setLoading(false));
  }, [open, token, productId]);

  const currentOverride = useMemo(() => {
    if (!currentOverrideBatchId) return null;
    return rows.find((r) => r.id === currentOverrideBatchId) ?? null;
  }, [rows, currentOverrideBatchId]);

  const handleSet = async (batchId: string) => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.products.setPosBatch({ token, productId, batchId });
      trackEvent(TRACKING_EVENTS.PRICING_OVERRIDE_USED);
      await onDone();
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) {
        const errors = (e.data as any)?.errors as Record<string, string[]> | undefined;
        const msg = errors?.batch_id?.[0] ?? e.message;
        setError(msg);
      } else {
        setError(e instanceof Error ? e.message : "Failed to set POS batch");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.products.clearPosBatch({ token, productId });
      trackEvent(TRACKING_EVENTS.FIFO_AUTO_APPLIED);
      await onDone();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear POS override");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Select POS Batch
            {isOverride ? <Badge variant="secondary">Override Active</Badge> : null}
          </DialogTitle>
          <div className="text-sm text-muted-foreground">
            Product: <span className="font-medium text-foreground">{productName}</span>
            {currentOverride ? (
              <span className="ml-2">
                (Current override: Batch #{currentOverride.id})
              </span>
            ) : null}
          </div>
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch</TableHead>
                <TableHead>Received</TableHead>
                <TableHead className="text-right">Qty remaining</TableHead>
                <TableHead className="text-right">Sell</TableHead>
                <TableHead className="text-right">MRP</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    Loading batches...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    No batches found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((b) => {
                  const remaining = qtyToNumber(b.quantity_remaining);
                  const outOfStock = remaining <= 0;
                  const isSelected = isOverride && currentOverrideBatchId === b.id;
                  return (
                    <TableRow key={b.id} className={isSelected ? "bg-accent/50" : undefined}>
                      <TableCell className="font-medium">
                        Batch #{b.id} {isSelected ? <Badge className="ml-2" variant="secondary">Selected</Badge> : null}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {b.received_at ? new Date(b.received_at).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell className="text-right">{b.quantity_remaining}</TableCell>
                      <TableCell className="text-right">{b.selling_price}</TableCell>
                      <TableCell className="text-right">{b.mrp}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant={isSelected ? "secondary" : "outline"}
                          disabled={submitting || outOfStock || isSelected}
                          onClick={() => handleSet(b.id)}
                          title={outOfStock ? "Out of stock" : undefined}
                        >
                          {outOfStock ? "Unavailable" : isSelected ? "Active" : "Set as POS Batch"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {isOverride && currentOverrideBatchId ? (
          <div className="text-sm text-muted-foreground">
            If the selected batch becomes empty, POS will automatically fall back to FIFO.
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" variant="outline" onClick={handleClear} disabled={submitting || !isOverride}>
            Use FIFO
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

